/**
 * VRG-677 — what Vera support can and cannot see inside a brand's space, in words.
 *
 * Inside a space (VERA-876) a staffer's permissions are REPLACED by the brand's
 * Vera-support grant (the "Vera support" column in Settings → Roles & permissions). Most
 * brands have granted nothing, so before this module the staffer met a bare
 * "Insufficient permissions" in Ask Vera and "You do not have permission to view this
 * page" in Settings, with nothing saying WHOSE permission it was or who could change it.
 *
 * Every sentence that explains that lives here, once (§28/§36), so the space banner, the
 * Ask Vera panel, its 403 fallback and the shared access-denied state all say the same
 * thing. Copy rules: §15 (no em-dash pileups), §27 (name the permission by the label an
 * admin sees in the role editor).
 *
 * PURE ON PURPOSE: no Vite aliases, no React, no `@repo/types` runtime import. Callers pass
 * labels in (see `utils/permissionLabels.ts`), which is what lets
 * `test/vera-support-access.guard.spec.ts` load this file under bare node.
 */

/** Where a brand admin changes the grant. One string so every message points at the same place. */
export const VERA_SUPPORT_GRANT_LOCATION = 'Settings → Roles & permissions'

/** The permission that gates Ask Vera itself (`@Permissions(AiView)` on the answer route). */
export const ASK_PERMISSION = 'ai_view' as const

/**
 * Why Ask Vera is unavailable, in the reader's own situation.
 *
 * - In a space: the brand has not granted it to Vera support, and only the BRAND can fix
 *   that, so the sentence names the brand's admin and where they do it.
 * - Outside a space: the reader's own role lacks it — the §27 wording, naming the label.
 */
export const askAccessDeniedMessage = ({
  inSpace,
  permissionLabel,
}: {
  inSpace: boolean
  permissionLabel: string
}): string =>
  inSpace
    ? `This brand hasn't given Vera support access to Ask Vera ('${permissionLabel}' permission). ` +
      `Their admin can turn it on in ${VERA_SUPPORT_GRANT_LOCATION}.`
    : `You need the '${permissionLabel}' permission. Ask an admin to grant it.`

/**
 * Decide, BEFORE sending, whether the Ask panel may send at all.
 *
 * `canUseAi` must be the EFFECTIVE permission: the space grant inside a space, the user's
 * own role outside one. That is exactly what `useCanAccess({action: 'ai_view'})` returns,
 * so the panel and the server's guard agree on the same set.
 */
export const resolveAskAccess = ({
  inSpace,
  canUseAi,
  permissionLabel,
}: {
  inSpace: boolean
  canUseAi: boolean
  permissionLabel: string
}): { allowed: true; message: null } | { allowed: false; message: string } =>
  canUseAi
    ? { allowed: true, message: null }
    : { allowed: false, message: askAccessDeniedMessage({ inSpace, permissionLabel }) }

/**
 * The 403 FALLBACK for the answer stream: the server refused after all (a grant changed
 * since the space was entered, a stale persisted permission list, a page that sent
 * anyway). Maps a permission refusal to the same sentence the panel shows up front.
 *
 * Only a PERMISSION 403 is rewritten. The guard has two other 403s on this path ("Not
 * allowed to enter company space", "Space company must match company context"), and
 * telling the staffer to ask the brand's admin about those would send them to the wrong
 * person, so they keep the server's own message. Returns null when the response should
 * be described the ordinary way.
 */
export const describeAskForbidden = ({
  status,
  serverMessage,
  inSpace,
  permissionLabel,
}: {
  status: number
  serverMessage: string | null
  inSpace: boolean
  permissionLabel: string
}): string | null => {
  if (status !== 403) return null
  const message = (serverMessage ?? '').toLowerCase()
  // "Insufficient permissions" (AuthGuard) and "Admin doesn't have required
  // permission(s): ai_view" (AuthAdminGuard) are the two permission refusals; an empty
  // body on a 403 is treated as one too, since that is the overwhelmingly likely cause.
  const isPermissionRefusal = message === '' || /permission/.test(message)
  if (!isPermissionRefusal) return null
  return askAccessDeniedMessage({ inSpace, permissionLabel })
}

/** "A", "A and B", "A, B and C". */
export const joinWithAnd = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

/**
 * The one- or two-sentence summary the space banner shows under "You are in X's space".
 *
 * @param areas     display labels of the permission GROUPS the grant touches, in matrix
 *                  order ("Company", "Products", "AI"…). Areas, not individual
 *                  permissions: eleven permission labels do not fit a banner, and an
 *                  area is what a staffer is deciding whether to open.
 * @param viewOnly  true when every granted permission is a read.
 * @param grantSize the raw number of granted permissions. Distinguishes "nothing granted"
 *                  from "granted, but nothing this build can name" (a permission newer
 *                  than the catalog), which must NOT be reported as no access.
 */
export const spaceGrantSummary = ({
  areas,
  viewOnly,
  grantSize,
}: {
  areas: readonly string[]
  viewOnly: boolean
  grantSize: number
}): string => {
  if (grantSize === 0) {
    return (
      "This brand hasn't granted Vera support any access yet. " +
      `Pages will be empty until their admin grants it in ${VERA_SUPPORT_GRANT_LOCATION}.`
    )
  }
  const scope = viewOnly ? 'view-only access' : 'access'
  const granted =
    areas.length > 0
      ? `This brand has granted Vera support ${scope} to ${joinWithAnd(areas)}.`
      : `This brand has granted Vera support limited ${scope}.`
  return `${granted} Anything else needs their admin to grant it in ${VERA_SUPPORT_GRANT_LOCATION}.`
}

/**
 * How many areas the banner's one-line headline names before collapsing the rest into
 * "+N more". Two, because the line shares its row with "Return to Internal" and, with the
 * Ask panel open, has roughly 400px; two area labels plus the scope fit, three do not.
 */
export const SPACE_HEADLINE_MAX_AREAS = 2

/**
 * VRG-677: the ONE line the space banner shows under "You are in X's space". The full
 * sentence (`spaceGrantSummary`) wrapped to ~8 lines with the Ask panel open, so the banner
 * leads with this and puts the full sentence behind "Show details".
 *
 * Same three cases as `spaceGrantSummary`, and the same rule that an unnamed grant is never
 * reported as no access.
 */
export const spaceGrantHeadline = ({
  areas,
  viewOnly,
  grantSize,
  maxAreas = SPACE_HEADLINE_MAX_AREAS,
}: {
  areas: readonly string[]
  viewOnly: boolean
  grantSize: number
  maxAreas?: number
}): string => {
  if (grantSize === 0) return 'No access granted yet, so pages will be empty.'
  const scope = viewOnly ? 'View-only access' : 'Access'
  if (areas.length === 0) return `Limited ${scope.toLowerCase()}.`
  if (areas.length <= maxAreas) return `${scope} to ${joinWithAnd(areas)}.`
  const shown = areas.slice(0, maxAreas).join(', ')
  return `${scope} to ${shown} +${areas.length - maxAreas} more.`
}

/**
 * The body of the shared access-denied state when the reader is Vera support in a space.
 * `subject` is lowercase ("company settings"); the label, when known, is the one the
 * brand's admin will look for in the matrix.
 */
export const spaceAccessDeniedText = ({
  subject,
  permissionLabel,
}: {
  subject: string
  permissionLabel?: string
}): string =>
  `This brand hasn't given Vera support access to ${subject}` +
  (permissionLabel ? ` ('${permissionLabel}' permission)` : '') +
  `. Their admin can turn it on in ${VERA_SUPPORT_GRANT_LOCATION}.`
